import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Settings, Instagram, Youtube, Globe, Facebook, Music2 } from '@/ui/icons'
import { Button } from '@/ui/components'
import { UserImageUpload } from './UserImageUpload'
import { UserProfileEditModal } from './UserProfileEditModal'
import { UserProfile } from '@/shared/api/users'
import { UserProfileApi } from '@/shared/api/users'
import { getUserBannerUrl, getUserProfileUrl } from '@/shared/utils/userImages'
import { useAuth } from '@/modules/auth'
import { useSubscription } from '@/modules/subscriptions'

interface UserProfileHeaderProps {
  profile: UserProfile
  userName: string
  isOwnProfile: boolean
  onProfileUpdate: (profile: UserProfile) => void
}

export function UserProfileHeader({ profile, userName, isOwnProfile, onProfileUpdate }: UserProfileHeaderProps) {
  const { getAccessToken } = useAuth()
  const { isPro } = useSubscription()
  const [isUploadingBanner, setIsUploadingBanner] = useState(false)
  const [isUploadingProfile, setIsUploadingProfile] = useState(false)
  const [isDeletingBanner, setIsDeletingBanner] = useState(false)
  const [isDeletingProfile, setIsDeletingProfile] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const userProfileApi = new UserProfileApi(getAccessToken)

  const bannerUrl = getUserBannerUrl(profile.banner_image_path)
  const profileUrl = getUserProfileUrl(profile.profile_image_path)

  const handleBannerUpload = async (file: File) => {
    setIsUploadingBanner(true)
    try {
      const updated = await userProfileApi.uploadBannerImage(profile.user_id, file)
      onProfileUpdate(updated)
    } finally {
      setIsUploadingBanner(false)
    }
  }

  const handleProfileUpload = async (file: File) => {
    setIsUploadingProfile(true)
    try {
      const updated = await userProfileApi.uploadProfileImage(profile.user_id, file)
      onProfileUpdate(updated)
    } finally {
      setIsUploadingProfile(false)
    }
  }

  const handleBannerDelete = async () => {
    setIsDeletingBanner(true)
    try {
      await userProfileApi.deleteBannerImage(profile.user_id)
      const updated = await userProfileApi.getUserProfile(profile.user_id)
      onProfileUpdate(updated)
    } finally {
      setIsDeletingBanner(false)
    }
  }

  const handleProfileDelete = async () => {
    setIsDeletingProfile(true)
    try {
      await userProfileApi.deleteProfileImage(profile.user_id)
      const updated = await userProfileApi.getUserProfile(profile.user_id)
      onProfileUpdate(updated)
    } finally {
      setIsDeletingProfile(false)
    }
  }

  const socialLinks = [
    { icon: Instagram, url: profile.instagram_url, label: 'Instagram' },
    { icon: Youtube, url: profile.youtube_url, label: 'YouTube' },
    { icon: Music2, url: profile.tiktok_url, label: 'TikTok' },
    { icon: Facebook, url: profile.facebook_url, label: 'Facebook' },
    { icon: Globe, url: profile.website_url, label: 'Website' }
  ].filter(link => link.url)

  const yearsOverlanding = profile.years_overlanding > 0 ? profile.years_overlanding : null
  const facts = [
    profile.location,
    yearsOverlanding && `${yearsOverlanding} ${yearsOverlanding === 1 ? 'year' : 'years'} overlanding`,
  ].filter(Boolean)

  return (
    <header>
      <div className="relative h-36 w-full overflow-hidden bg-earth-100 md:h-56">
        {isOwnProfile ? (
          <UserImageUpload
            type="banner"
            currentImageUrl={bannerUrl}
            onUpload={handleBannerUpload}
            onDelete={bannerUrl ? handleBannerDelete : undefined}
            isUploading={isUploadingBanner}
            isDeleting={isDeletingBanner}
          />
        ) : (
          bannerUrl && <img src={bannerUrl} alt="" className="h-full w-full object-cover" />
        )}
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <div className="flex items-end justify-between gap-4">
          <div className="relative z-10 -mt-14 flex-shrink-0 md:-mt-16">
            {isOwnProfile ? (
              <UserImageUpload
                type="profile"
                currentImageUrl={profileUrl}
                onUpload={handleProfileUpload}
                onDelete={profileUrl ? handleProfileDelete : undefined}
                isUploading={isUploadingProfile}
                isDeleting={isDeletingProfile}
              />
            ) : profileUrl ? (
              <div className="h-32 w-32 overflow-hidden rounded-full border-4 border-background">
                <img src={profileUrl} alt="" className="h-full w-full object-cover" />
              </div>
            ) : (
              <div className="flex h-32 w-32 items-center justify-center rounded-full border-4 border-background bg-earth-100">
                <span className="text-4xl font-semibold text-earth-600" aria-hidden>
                  {userName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {isOwnProfile && (
            <Button variant="outline" size="sm" onClick={() => setIsEditModalOpen(true)} className="mt-3 gap-1.5">
              <Settings className="h-4 w-4" aria-hidden />
              Edit profile
            </Button>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="min-w-0 break-words text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
            {userName}
          </h1>
          {isOwnProfile &&
            (isPro ? (
              <span className="rounded-full bg-earth-100 px-2.5 py-0.5 text-xs font-medium text-foreground">
                Pro member
              </span>
            ) : (
              <Link
                to="/pricing"
                className="rounded-full bg-earth-100 px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors hover:bg-earth-200"
              >
                Free plan · Upgrade
              </Link>
            ))}
        </div>

        {facts.length > 0 && <p className="mt-1 break-words text-sm text-earth-600">{facts.join(' · ')}</p>}

        {profile.bio && (
          <p className="mt-3 max-w-2xl whitespace-pre-line break-words text-sm leading-relaxed text-foreground">
            {profile.bio}
          </p>
        )}

        {socialLinks.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1">
            {socialLinks.map((link) => (
              <a
                key={link.label}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-9 w-9 items-center justify-center rounded-md text-earth-600 transition-colors hover:bg-earth-100 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={link.label}
              >
                <link.icon className="h-4 w-4" aria-hidden />
              </a>
            ))}
          </div>
        )}
      </div>

      {isOwnProfile && (
        <UserProfileEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          profile={profile}
          onProfileUpdated={onProfileUpdate}
        />
      )}
    </header>
  )
}
